// Website content from the CMS. One request per visit (shared by every component and kept in memory), merged over
// the built-in copy in siteContentDefaults.js. If the request fails, the built-in copy is shown, so the site never
// depends on the content API. Values are plain text: render them as React text, never as HTML.
//
// Preview: an admin's "Preview" button opens a page with ?preview=<token>. The token is kept for this browser tab
// (sessionStorage) and the draft values are shown instead of the published ones, with a bar to leave preview.
import { useEffect, useMemo, useState } from 'react';
import { SITE_CONTENT_DEFAULTS } from './siteContentDefaults';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const PREVIEW_KEY = 'voip2_cms_preview';
const CACHE_KEY = 'voip2_site_content';

const storage = () => {
  try { return window.sessionStorage; } catch (e) { return null; }
};

const readPreviewToken = () => {
  if (typeof window === 'undefined') return null;
  const s = storage();
  try {
    const param = new URLSearchParams(window.location.search).get('preview');
    if (param && /^[A-Za-z0-9_-]{32,64}$/.test(param)) {
      if (s) s.setItem(PREVIEW_KEY, param);
      return param;
    }
    return s ? s.getItem(PREVIEW_KEY) : null;
  } catch (e) {
    return null;
  }
};

const state = { data: null, promise: null, preview: false, listeners: new Set() };

// Published content from earlier in this visit, so later page loads do not flash the built-in copy first.
try {
  const cached = storage() && storage().getItem(CACHE_KEY);
  if (cached && !readPreviewToken()) state.data = JSON.parse(cached);
} catch (e) { /* ignore a bad cache */ }

const fetchJson = async (path) => {
  const r = await fetch(`${BACKEND_URL}${path}`);
  if (!r.ok) throw new Error(String(r.status));
  return r.json();
};

const load = () => {
  if (state.promise) return state.promise;
  const token = readPreviewToken();
  state.promise = (async () => {
    if (token) {
      try {
        const data = await fetchJson(`/api/site-content/preview/${token}`);
        state.preview = true;
        return data;
      } catch (e) {
        try { storage() && storage().removeItem(PREVIEW_KEY); } catch (err) { /* ignore */ }
      }
    }
    const data = await fetchJson('/api/site-content');
    try { storage() && storage().setItem(CACHE_KEY, JSON.stringify(data)); } catch (e) { /* ignore */ }
    return data;
  })()
    .then((data) => {
      state.data = data && typeof data.pages === 'object' ? data : { pages: {} };
      state.listeners.forEach((fn) => fn(state.data));
      return state.data;
    })
    .catch(() => state.data);
  return state.promise;
};

export const exitPreview = () => {
  try { storage() && storage().removeItem(PREVIEW_KEY); } catch (e) { /* ignore */ }
  const url = new URL(window.location.href);
  url.searchParams.delete('preview');
  window.location.replace(url.toString());
};

/** https:// URL or a path on this site; anything else (javascript:, data:, //host) is refused. */
export const isSafeUrl = (v) => typeof v === 'string'
  && (/^\/(?![/\\])[^\s<>"'`]*$/.test(v) || /^https:\/\/[a-zA-Z0-9.-]+(?::\d+)?(?:[/?#][^\s<>"'`]*)?$/.test(v));

/** Uploaded media (/uploads/x or /api/media/x) is served by the backend; other paths are the site's own files. */
export const resolveMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/media/')) return `${BACKEND_URL}${url}`;
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}/api/media/${url.replace('/uploads/', '')}`;
  return url;
};

const typeMatches = (value, fallback) => {
  if (Array.isArray(fallback)) return Array.isArray(value) && value.every((v) => typeof v === 'string');
  return typeof value === 'string';
};

const URL_KEY = /(^|\.)(image|video)$|_url$/;

/**
 * Content getter for one page: c('hero.headline') returns the published (or previewed) value, or the built-in copy.
 * URL and image fields fall back to the built-in value if the stored one is not a safe URL.
 */
export const useSiteContent = (page) => {
  const [data, setData] = useState(state.data);
  useEffect(() => {
    const listener = (d) => setData(d);
    state.listeners.add(listener);
    load();
    return () => { state.listeners.delete(listener); };
  }, []);
  return useMemo(() => {
    const defaults = SITE_CONTENT_DEFAULTS[page] || {};
    const values = (data && data.pages && data.pages[page]) || {};
    return (key) => {
      const fallback = defaults[key];
      const value = values[key];
      if (value === undefined || value === null || !typeMatches(value, fallback)) return fallback;
      if (URL_KEY.test(key) && value !== '' && !isSafeUrl(value)) return fallback;
      return value;
    };
  }, [page, data]);
};

/** True while the page is showing draft content from a preview link. */
export const usePreviewMode = () => {
  const [preview, setPreview] = useState(state.preview);
  useEffect(() => {
    const listener = () => setPreview(state.preview);
    state.listeners.add(listener);
    load();
    return () => { state.listeners.delete(listener); };
  }, []);
  return preview;
};

/** A product page's data object (data/*.js) with the CMS hero, key stats and intro applied. */
// The headline ending follows the highlighted words: a space goes in between unless it starts with punctuation.
const joinAfter = (after) => (!after ? '' : /^[,.;:!?)]/.test(after) ? after : ` ${after}`);

export const withProductContent = (c, D, { trust = true } = {}) => ({
  ...D,
  hero: {
    ...D.hero,
    title: c('hero.headline'),
    highlight: c('hero.highlight'),
    after: joinAfter(c('hero.after')),
    sub: c('hero.sub'),
    bullets: c('hero.bullets'),
  },
  trust: trust && D.trust
    ? D.trust.map((t, i) => ({ value: c(`stats.${i + 1}.value`) ?? t.value, label: c(`stats.${i + 1}.label`) ?? t.label }))
    : D.trust,
  intro: {
    ...D.intro,
    title: c('intro.title'),
    paragraphs: c('intro.paragraphs'),
    bullets: c('intro.bullets'),
  },
});
